import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import MixinGame from '@/mixins/game'
import { boardWidthOriginal, boardHeightOriginal } from '@constants/board'
import { VageDrag, vageDragEvents, VageDragEvent } from '@/plugins/drag/manager'
import events from '@/shared/events'

@Component
export default class Game extends mixins(MixinGame) {
  private panContainerWidth: number = 0
  private panContainerHeight: number = 0
  private viewPortWidth: number = 0
  private viewPortHeight: number = 0
  private boardWidth: number = 0
  private boardHeight: number = 0

  @Watch('turnID', { immediate: true })
  public onTurnChange() {
    this.$nextTick(() => {
      const stock: HTMLElement = document.querySelector('.stock') as HTMLElement
      const sidebar: HTMLElement = document.querySelector('.sidebar') as HTMLElement
      const stockRect: DOMRect = stock.getBoundingClientRect() as DOMRect
      const sidebarRect: DOMRect = sidebar.getBoundingClientRect() as DOMRect

      this.viewPortWidth = window.innerWidth - sidebarRect.width
      this.viewPortHeight = window.innerHeight - stockRect.height
    })
  }

  @Watch('boardZoomFactor', { immediate: true })
  public onBoardZoomChange() {
    this.boardWidth = boardWidthOriginal * this.boardZoomFactor
    this.boardHeight = boardHeightOriginal * this.boardZoomFactor
  }

  get panStyle(): object {
    return {
      transform: `translate(${this.panX}px, ${this.panY}px)`,
      width: `${this.panWidth}px`,
      height: `${this.panHeight}px`,
    }
  }

  get panX() {
    return Math.abs(this.boardX) * (this.panContainerWidth / this.boardWidth)
  }

  get panY() {
    return Math.abs(this.boardY) * (this.panContainerHeight / this.boardHeight)
  }

  get panWidth() {
    const viewportBoardFactorWidth = this.viewPortWidth / this.boardWidth
    return this.panContainerWidth * viewportBoardFactorWidth
  }

  get panHeight() {
    const viewportBoardFactorHeight = this.viewPortHeight / this.boardHeight
    return this.panContainerHeight * viewportBoardFactorHeight
  }

  get zoom() {
    const minZoom = this.getMinZoom()
    const factor = (this.boardZoomFactor - minZoom) / (1 - minZoom)

    return Math.round(factor * 100)
  }

  set zoom(value) {
    this.$bus.$emit(events.BOARD_ZOOM, {
      factor: value / 100,
      x: this.viewPortWidth * 0.5,
      y: this.viewPortHeight * 0.5,
    })
  }

  public mounted() {
    this.initVariables()
    this.bindEvents()
  }

  private initVariables() {
    const panZoom: HTMLElement = this.$refs.panZoom as HTMLElement
    const panZoomRect: DOMRect = panZoom.getBoundingClientRect() as DOMRect

    this.panContainerWidth = panZoomRect.width
    this.panContainerHeight = panZoomRect.height
  }

  private bindEvents() {
    const panner: HTMLElement = this.$refs.panner as HTMLElement
    const vageDrag: VageDrag = this.$drag.add(panner, {
      moveToTop: false,
    })

    vageDrag.on(vageDragEvents.DRAG, this.onDrag)
  }

  private onDrag(event: VageDragEvent) {
    let boardX = 0
    let boardY = 0

    if (this.panWidth < this.panContainerWidth) {
      const panTravelFactorX = event.x / (this.panContainerWidth - this.panWidth)
      boardX = (this.boardWidth - this.viewPortWidth) * panTravelFactorX * -1
    }

    if (this.panHeight < this.panContainerHeight) {
      const panTravelFactorY = event.y / (this.panContainerHeight - this.panHeight)
      boardY = (this.boardHeight - this.viewPortHeight) * panTravelFactorY * -1
    }

    this.setBoardX(boardX)
    this.setBoardY(boardY)

    this.$feathers.service('api/viewport')
      .update(this.tableID, {
        x: boardX,
        y: boardY,
        zoom: this.boardZoomFactor,
      })
  }

  private getMinZoom() {
    const container: HTMLElement = document.querySelector('.board-container') as HTMLElement

    if (!container) {
      return 0
    }

    return container.getBoundingClientRect().width / boardWidthOriginal
  }
}
